/**
 * LLM-9E — optional ephemeral memory suggestion card with authority-safe promotion.
 */
import { useCallback, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  buildSessionMessageRequest,
  CONFIRM_REPLY,
  PROMOTION_UI_STATE,
  REJECT_REPLY,
} from "@/lib/memorySuggestionPromotion";

const STATUS_COPY = {
  [PROMOTION_UI_STATE.PROMOTION_STARTING]: "Preparing confirmation…",
  [PROMOTION_UI_STATE.CONFIRMING]: "Saving preference…",
  [PROMOTION_UI_STATE.REJECTING]: "Cancelling…",
};

export const MemorySuggestionCard = ({
  suggestion,
  onCreateSession,
  onSendSessionMessage,
  onDismissSuggestion,
  testId = "memory-suggestion-card",
}) => {
  const [uiState, setUiState] = useState(PROMOTION_UI_STATE.IDLE);
  const [confirmationSummary, setConfirmationSummary] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const sessionRef = useRef({ conversationId: null, revision: null });
  const requestInFlightRef = useRef(false);
  const dismissFeedbackSentRef = useRef(false);

  const isPending = [
    PROMOTION_UI_STATE.PROMOTION_STARTING,
    PROMOTION_UI_STATE.CONFIRMING,
    PROMOTION_UI_STATE.REJECTING,
  ].includes(uiState);

  const ensureSession = useCallback(async () => {
    if (sessionRef.current.conversationId && sessionRef.current.revision) {
      return sessionRef.current;
    }
    const session = await onCreateSession();
    sessionRef.current = {
      conversationId: session.conversation_id,
      revision: session.revision,
    };
    return sessionRef.current;
  }, [onCreateSession]);

  const submitSessionMessage = useCallback(async (message) => {
    const { conversationId, revision } = await ensureSession();
    const response = await onSendSessionMessage(
      conversationId,
      buildSessionMessageRequest({
        message,
        expectedRevision: revision,
      }),
    );
    sessionRef.current.revision = response.revision;
    return response;
  }, [ensureSession, onSendSessionMessage]);

  const handleNotNow = () => {
    if (requestInFlightRef.current) return;
    setUiState(PROMOTION_UI_STATE.DISMISSED);
    const feedbackRef = suggestion?.feedbackRef;
    if (
      feedbackRef
      && typeof onDismissSuggestion === "function"
      && !dismissFeedbackSentRef.current
    ) {
      dismissFeedbackSentRef.current = true;
      void onDismissSuggestion({ feedbackRef }).catch(() => {
        // Local dismissal stands; suppression feedback is best-effort only.
      });
    }
  };

  const handleRemember = async () => {
    if (requestInFlightRef.current || uiState !== PROMOTION_UI_STATE.IDLE) {
      return;
    }
    requestInFlightRef.current = true;
    setErrorMessage(null);
    setUiState(PROMOTION_UI_STATE.PROMOTION_STARTING);
    try {
      const response = await submitSessionMessage(suggestion.promotionCommand);
      if (response.confirmation_required) {
        setConfirmationSummary(
          response.pending_action_summary
            || "Would you like me to save this coaching preference?",
        );
        setUiState(PROMOTION_UI_STATE.WAITING_FOR_CONFIRMATION);
      } else if (response.executed) {
        setUiState(PROMOTION_UI_STATE.SAVED);
      } else {
        setUiState(PROMOTION_UI_STATE.ERROR_PROMOTION);
        setErrorMessage("Could not start memory confirmation. Try again.");
      }
    } catch (err) {
      setUiState(PROMOTION_UI_STATE.ERROR_PROMOTION);
      setErrorMessage(
        err?.aiCoachError?.message
          || "Could not start memory confirmation. Try again.",
      );
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleConfirm = async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setErrorMessage(null);
    setUiState(PROMOTION_UI_STATE.CONFIRMING);
    try {
      const response = await submitSessionMessage(CONFIRM_REPLY);
      if (response.executed) {
        setUiState(PROMOTION_UI_STATE.SAVED);
      } else {
        setUiState(PROMOTION_UI_STATE.ERROR_UNKNOWN);
        setErrorMessage(
          "Couldn't confirm the save. Check the coaching conversation and try again.",
        );
      }
    } catch (err) {
      setUiState(PROMOTION_UI_STATE.ERROR_UNKNOWN);
      setErrorMessage(
        "Couldn't confirm the save. Check the coaching conversation and try again.",
      );
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleReject = async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setErrorMessage(null);
    setUiState(PROMOTION_UI_STATE.REJECTING);
    try {
      await submitSessionMessage(REJECT_REPLY);
      setUiState(PROMOTION_UI_STATE.NOT_SAVED);
    } catch (err) {
      setUiState(PROMOTION_UI_STATE.ERROR_UNKNOWN);
      setErrorMessage(
        "Couldn't confirm the outcome. Check the coaching conversation and try again.",
      );
    } finally {
      requestInFlightRef.current = false;
    }
  };

  if (uiState === PROMOTION_UI_STATE.DISMISSED) {
    return null;
  }

  return (
    <aside
      data-testid={testId}
      aria-label="Possible coaching preference"
      className="mt-4 border border-dark-border bg-dark-surface-2 rounded-lg p-4"
    >
      <p className="text-[11px] uppercase tracking-[0.25em] text-dark-text-muted">
        Possible coaching preference
      </p>
      <p
        data-testid={`${testId}-body`}
        className="mt-2 text-sm text-dark-text-secondary whitespace-pre-wrap break-words"
      >
        {suggestion.observationText}
      </p>

      {uiState === PROMOTION_UI_STATE.SAVED && (
        <p
          data-testid={`${testId}-saved`}
          className="mt-3 text-sm text-analytics-green"
          role="status"
        >
          Saved
        </p>
      )}

      {uiState === PROMOTION_UI_STATE.NOT_SAVED && (
        <p
          data-testid={`${testId}-not-saved`}
          className="mt-3 text-sm text-dark-text-muted"
          role="status"
        >
          Not saved
        </p>
      )}

      {errorMessage && (
        <p
          data-testid={`${testId}-error`}
          className="mt-3 text-sm text-analytics-amber"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      {STATUS_COPY[uiState] && (
        <p
          data-testid={`${testId}-status`}
          className="mt-3 text-sm text-dark-text-muted flex items-center gap-2"
          role="status"
        >
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          {STATUS_COPY[uiState]}
        </p>
      )}

      {uiState === PROMOTION_UI_STATE.WAITING_FOR_CONFIRMATION && (
        <div className="mt-4 space-y-3" data-testid={`${testId}-confirmation`}>
          <p className="text-sm text-dark-text-secondary">
            {confirmationSummary}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={isPending}
              onClick={handleConfirm}
              data-testid={`${testId}-confirm`}
            >
              Confirm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isPending}
              onClick={handleReject}
              data-testid={`${testId}-cancel`}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {uiState === PROMOTION_UI_STATE.IDLE
        || uiState === PROMOTION_UI_STATE.ERROR_PROMOTION ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={isPending}
              onClick={handleRemember}
              data-testid={`${testId}-remember`}
            >
              Remember this
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isPending}
              onClick={handleNotNow}
              data-testid={`${testId}-not-now`}
            >
              Not now
            </Button>
          </div>
        ) : null}

      {uiState === PROMOTION_UI_STATE.SAVED ? (
        <div className="mt-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled
            data-testid={`${testId}-remember`}
          >
            Remember this
          </Button>
        </div>
      ) : null}
    </aside>
  );
};
